// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ASSET_MOVE_CONFLICT_CODE } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getMoveConflictMessage,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormCurrencyTextField } from '@/components/form-currency-text-field';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import {
  SerializableCurrency,
  clearableCurrencyInputSchema,
} from '@/currency/api/types';
import { toCurrencyInputValue } from '@/currency/utilities/to-form-value';
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { useShipmentInventory } from '@/inventory/hooks/use-shipment-inventory';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  ShipShipmentRequest,
  shipShipment,
} from '@/shipment/api/ship-shipment';
import { Shipment } from '@/shipment/api/types';
import { ShipDialogExistingItem } from '@/shipment/components/ship-dialog-existing-item';
import { ShipDialogNewItem } from '@/shipment/components/ship-dialog-new-item';
import { SerialCollectionDrawer } from '@/shipment/components/ship-serial-collection-drawer';
import { ViewSerialsDrawer } from '@/shipment/components/view-serials-drawer';
import {
  shipmentDetailQueryKey,
  useShipment,
} from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import {
  SparseItem,
  sparseItemSchema,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

const shipItemSchema = z.object({
  item: sparseItemSchema.nullable(),
  quantity: z
    .number()
    .min(0, {
      message: 'Must be zero or more',
    })
    .nullable(),
  initialQuantity: z.number().nullable(),
  externalReference: z.string().nullable(),
  serials: z.array(z.string()).optional(),
  isOriginalItem: z.boolean().optional(),
});

// Create schema factory that accepts inventory map for validation
const createShipFormSchema = (
  inventoryMap: Map<string, Map<string, number>>,
  isEditMode: boolean
) =>
  z
    .object({
      shipWarehouse: sparseLocationSchema.nullable(),
      items: z.array(shipItemSchema),
      shippedAt: dateTimeSchema.nullable(),
      shipNote: z.string().nullable().optional(),
      shippingCost: clearableCurrencyInputSchema,
    })
    .superRefine((data, ctx) => {
      // Run validation on submission so we can still have null as a state in the form.
      // The nulls are legal till the form is submitted then errors should be shown.

      // Check if shipWarehouse is set (not null)
      if (!data.shipWarehouse) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Shipping location is required',
          path: ['shipWarehouse'],
        });
      }

      // Check if shippedAt is set (not null)
      if (!data.shippedAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Ship date is required',
          path: ['shippedAt'],
        });
      }

      // Check if items array has at least one item
      if (data.items && data.items.length > 0) {
        // Check that every item has its item property not null
        data.items.forEach((item, index) => {
          if (!item.item) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Item is required',
              path: ['items', index, 'item'],
            });
          }
          if (item.quantity === null || item.quantity < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Must be zero or more',
              path: ['items', index, 'quantity'],
            });
          }

          // Check inventory availability for bulk items (only when not in edit mode)
          // Bulk items (!hasAssets) require inventory validation
          if (
            !isEditMode &&
            item.item &&
            !item.item.hasAssets &&
            item.quantity &&
            item.quantity > 0 &&
            data.shipWarehouse
          ) {
            const availableQty =
              inventoryMap.get(data.shipWarehouse.id)?.get(item.item.id) ?? 0;
            if (item.quantity > availableQty) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Insufficient inventory (need ${item.quantity}, have ${availableQty})`,
                path: ['items', index, 'quantity'],
              });
            }
          }

          // Strict 1:1 serial:quantity only applies to tracked devices; bulk is lenient.
          const requiresStrictSerialCount =
            item.item?.hasAssets && item.item?.isPhysical;

          if (
            requiresStrictSerialCount &&
            item.quantity !== null &&
            item.quantity > 0
          ) {
            const serialCount = item.serials?.length ?? 0;
            if (serialCount !== item.quantity) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Serials required: ${serialCount} entered, ${item.quantity} needed`,
                path: ['items', index, 'serials'],
              });
            }
          }
        });
      }
    });

const _shipFormSchema = createShipFormSchema(new Map(), false);

export type ShipDialogFormData = Omit<
  z.infer<typeof _shipFormSchema>,
  'shippingCost'
> & {
  shippingCost: SerializableCurrency;
};
type ShipDialogFormOutput = z.output<typeof _shipFormSchema>;

// Helper type for accessing form errors for items
export interface ShipDialogItemError {
  item?: { message: string };
  quantity?: { message: string };
  serials?: { message: string };
  externalReference?: { message: string };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipment?: Shipment;
}

// Internal component that gets remounted when shipment changes
function OutboundShipDialogForm(props: Props) {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const defaultCurrencyCode = user.organization.defaultCurrency;
  const isEditMode = !!props.shipment?.shippedAt;
  const dialogRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(props.isOpen);

  // Fetch detailed shipment data when in edit mode (includes serials)
  const { data: detailedShipment } = useShipment(props.shipment?.key, {
    enabled: props.isOpen && isEditMode,
  });

  // Use detailed shipment data if available, otherwise fallback to props
  const shipmentData = detailedShipment || props.shipment;

  const [serialDrawerOpen, setSerialDrawerOpen] = useState<number | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const { showToast } = useToast();

  const getDefaultValues = useCallback(() => {
    if (isEditMode && shipmentData) {
      return {
        items: shipmentData.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          initialQuantity: lineItem.quantity,
          quantity: lineItem.quantity,
          externalReference: lineItem.externalReference,
          serials: shipmentLineAllowsSerials(lineItem.item)
            ? lineItem.serials || []
            : [],
          isOriginalItem: true,
        })),
        shipNote: shipmentData.shipmentNote,
        shipWarehouse: shipmentData.fromLocation,
        shippedAt: shipmentData.shippedAt,
        shippingCost: toCurrencyInputValue(
          shipmentData.shippingCost,
          defaultCurrencyCode
        ),
      };
    }
    return {
      items:
        props.shipment?.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          initialQuantity: lineItem.quantity,
          quantity: lineItem.quantity,
          externalReference: lineItem.externalReference,
          serials: [],
          isOriginalItem: true,
        })) || [],
      shipNote: null,
      shipWarehouse: props.shipment?.fromLocation ?? null,
      shippedAt: null,
      shippingCost: toCurrencyInputValue(
        props.shipment?.shippingCost ?? null,
        defaultCurrencyCode
      ),
    };
  }, [isEditMode, shipmentData, props.shipment, defaultCurrencyCode]);

  // Use inventory hook - fetches all inventory at the ship location
  // This must be called before form initialization so inventoryMap is available for the schema
  const {
    inventoryMap,
    serialsMap,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
  } = useShipmentInventory({
    location: props.shipment?.fromLocation,
  });

  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState,
    trigger,
    setError,
  } = useForm<ShipDialogFormData, unknown, ShipDialogFormOutput>({
    resolver: zodResolver(createShipFormSchema(inventoryMap, isEditMode)),
    defaultValues: getDefaultValues(),
  });

  const {
    fields: itemsField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  // Watch all items to exclude already selected ones from the dropdown
  const watchedItems = watch('items');

  // Watch form values for inventory checking
  const shipWarehouse = watch('shipWarehouse');

  // Reset form when the dialog opens so edits made after mount are reflected
  useEffect(() => {
    const isOpening = props.isOpen && !wasOpenRef.current;
    wasOpenRef.current = props.isOpen;

    if (isOpening) {
      reset(getDefaultValues());
    }
  }, [props.isOpen, props.shipment, isEditMode, reset, getDefaultValues]);

  // Trigger revalidation when inventory data loads/changes
  useEffect(() => {
    if (!isLoadingInventory && inventoryMap.size > 0) {
      trigger('items');
    }
  }, [inventoryMap, isLoadingInventory, trigger]);

  // Get available serials from inventory data when drawer opens
  const availableSerials = useMemo(() => {
    if (serialDrawerOpen === null) {
      return [];
    }

    const currentItem = watchedItems?.[serialDrawerOpen];
    const locationId = getValues('shipWarehouse')?.id;
    // Device items (hasAssets && isPhysical) have serials
    const deviceId =
      currentItem?.item?.hasAssets && currentItem?.item?.isPhysical
        ? currentItem?.item?.id
        : undefined;

    if (!locationId || !deviceId) {
      return [];
    }

    const locationSerials = serialsMap.get(locationId);
    if (!locationSerials) {
      return [];
    }

    return locationSerials.get(deviceId) || [];
  }, [serialDrawerOpen, watchedItems, getValues, serialsMap]);

  const handleClose = () => {
    resetFormToOriginal();
    setShowSnackbar(false);
    setInlineErrorMessage(null);
    props.onClose();
  };

  const shipShipmentMutation = useMutation({
    mutationFn: (data: ShipShipmentRequest) =>
      shipShipment(props.shipment?.key ?? '', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(props.shipment?.key ?? ''),
      });
      queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      invalidateInventory(queryClient);
      showToast({
        element: (
          <Typography>
            {isEditMode
              ? 'Shipment updated successfully'
              : 'Shipment marked as shipped successfully'}
          </Typography>
        ),
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
      // Reset form after dialog is closed
      setTimeout(() => {
        resetFormToOriginal();
      }, 0);
    },
    onError: (error: AxiosError) => {
      if (isPlanLimitError(error)) {
        return;
      }

      const apiError = handleAPIError(error);

      // A move conflict is the user's to fix and names the conflicting serials, so show
      // the serial-aware message inline like the delivery and receive dialogs
      if (apiError.apiStatusCode === ASSET_MOVE_CONFLICT_CODE) {
        setInlineErrorMessage(
          getMoveConflictMessage(apiError.conflictingAssets)
        );
        setShowSnackbar(false);
        return;
      }

      // Validation error 2220: ship date is after delivery date.
      // Inline on the date field; no snackbar.
      if (apiError.apiStatusCode === 2220) {
        setError('shippedAt', {
          type: 'manual',
          message: 'Ship date cannot be after the delivery date',
        });
        setShowSnackbar(false);
        setInlineErrorMessage(null);
        return;
      }
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setInlineErrorMessage(curated);
        setShowSnackbar(false);
        return;
      }
      if (!apiError.sentToSentry) {
        captureException(error);
      }
      setInlineErrorMessage(null);
      setShowSnackbar(true);
    },
  });

  const onSubmit = async (data: ShipDialogFormOutput) => {
    // Validation now happens in the Zod schema, form won't submit if invalid
    // shipWarehouse and shippedAt are guaranteed to be non-null by schema validation
    // Note: hasAttemptedSubmit is set in the button onClick handler

    // When editing an already shipped shipment, omit quantity and serials
    // as they cannot be changed. Reference IDs and shippedAt can still be updated.

    // Capture timezone before converting to UTC
    // Ensure seconds and milliseconds are 0
    const shippedAt = data.shippedAt?.set({ second: 0, millisecond: 0 });
    const shippedAtTimezone = shippedAt?.zoneName;

    const shipData: ShipShipmentRequest = isEditMode
      ? {
          shipLocationId: data.shipWarehouse!.id,
          items: data.items.map((item) => ({
            itemId: item.item?.id ?? '',
            externalReference: item.externalReference ?? undefined,
            // Omit quantity and serials when editing
          })),
          shipmentNote: data.shipNote ?? undefined,
          shippedAt: shippedAt!.toUTC(),
          shippedAtTimezone: shippedAtTimezone,
        }
      : {
          shipLocationId: data.shipWarehouse!.id,
          items: data.items
            .filter(
              (item) =>
                item.quantity !== null &&
                item.quantity !== undefined &&
                item.quantity > 0
            )
            .map((item) => ({
              itemId: item.item?.id ?? '',
              quantity: item.quantity!,
              serials: shipmentLineAllowsSerials(item.item)
                ? (item.serials ?? [])
                : undefined,
              externalReference: item.externalReference ?? undefined,
            })),
          shipmentNote: data.shipNote ?? undefined,
          shippedAt: shippedAt!.toUTC(),
          shippedAtTimezone: shippedAtTimezone,
          shippingCost: data.shippingCost ?? null,
        };

    if (isEditMode && formState.dirtyFields.shippingCost) {
      shipData.shippingCost = data.shippingCost ?? null;
    }

    return shipShipmentMutation.mutate(shipData);
  };

  const getHiddenItemsForIndex = (currentIndex: number): SparseItem[] => {
    return (
      watchedItems
        ?.map((item, index) => {
          if (index === currentIndex || !item.item) return null;
          return item.item;
        })
        .filter((item): item is SparseItem => item !== null) || []
    );
  };

  const addNewItem = () => {
    append({
      item: null,
      quantity: 1,
      initialQuantity: null,
      externalReference: null,
      serials: [],
      isOriginalItem: false, // Mark as newly added item
    });
  };

  const resetFormToOriginal = () => {
    // Reset to original shipment items only (remove any added items)
    if (props.shipment) {
      reset({
        items: props.shipment.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          initialQuantity: lineItem.quantity,
          quantity: lineItem.quantity,
          externalReference: null,
          serials: [],
          isOriginalItem: true,
        })),
        shipNote: null,
        shipWarehouse: props.shipment?.fromLocation ?? null,
        shippedAt: null,
        shippingCost: toCurrencyInputValue(
          props.shipment?.shippingCost ?? null,
          defaultCurrencyCode
        ),
      });
    } else {
      reset();
    }
  };

  return (
    <>
      <Dialog
        ref={dialogRef}
        open={props.isOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          // Route Escape through handleClose so it clears state like Cancel does; the
          // dialog stays mounted for the same shipment and would otherwise show stale copy
          handleClose();
        }}
        fullWidth
        maxWidth={serialDrawerOpen !== null ? 'lg' : 'sm'}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(onSubmit),
          sx: {
            display: 'flex',
            flexDirection: 'row',
            height: '90vh',
            maxHeight: '90vh',
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <DialogTitle sx={{ px: 3, pt: 3 }}>
            {isEditMode
              ? 'Edit outbound shipment details'
              : 'Mark shipment as shipped'}
          </DialogTitle>
          <DialogContent dividers sx={{ px: 3, pb: 1 }}>
            <Stack gap={3}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {`Record shipment details for ${props.shipment?.shipmentNumber ?? 'shipment'}${props.shipment?.fulfillmentOrder?.fulfillmentOrderNumber ? ' - ' + props.shipment?.fulfillmentOrder.fulfillmentOrderNumber : ''}`}
                </Typography>
              </Box>
              <ConditionalTooltip
                arrow
                tooltipText={
                  isEditMode
                    ? 'Location cannot be modified once shipment has been shipped'
                    : undefined
                }
                sx={{ width: '100%' }}
              >
                <Box sx={{ width: '100%' }}>
                  <FormLocationSearch
                    name={'shipWarehouse'}
                    control={control}
                    label="Shipping location"
                    disabled={isEditMode}
                    onCreate={(name: string) => {
                      const url = getNewLocationURL(name);
                      window.open(url, '_blank');
                    }}
                  />
                </Box>
              </ConditionalTooltip>
              <FormDateTimePicker
                disableFuture
                name="shippedAt"
                label="Ship date"
                control={control}
                isDisabled={false}
                maximumDate={
                  props.shipment?.deliveredAt ??
                  props.shipment?.receivedAt ??
                  undefined
                }
              />
              <FormCurrencyTextField
                control={control}
                fullWidth
                label="Shipping cost"
                name="shippingCost"
                placeholder="0.00"
              />
              <Stack gap={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ pl: 1 }}
                >
                  <Typography variant="subtitle2">Items</Typography>
                  {!props.shipment?.shippedAt && (
                    <Button
                      type="button"
                      size="small"
                      startIcon={<AddRounded />}
                      onClick={addNewItem}
                      variant="outlined"
                    >
                      Add item
                    </Button>
                  )}
                </Stack>
                <Stack gap={2}>
                  {itemsField.map((field, index) => {
                    const watchedItem = watchedItems?.[index];

                    return isEditMode || field.isOriginalItem ? (
                      <ShipDialogExistingItem
                        key={field.id}
                        index={index}
                        item={field}
                        watchedItem={watchedItem}
                        control={control}
                        formState={formState}
                        isEditMode={isEditMode}
                        onSerialDrawerOpen={setSerialDrawerOpen}
                        serialDrawerOpen={serialDrawerOpen}
                        onClearErrors={(idx) => {
                          clearErrors(`items.${idx}.serials`);
                        }}
                        trigger={trigger}
                        invalidSerials={undefined}
                        shipWarehouse={shipWarehouse}
                        inventoryMap={inventoryMap}
                        isLoadingInventory={isLoadingInventory}
                        isFetchingInventory={isFetchingInventory}
                        hasAttemptedSubmit={hasAttemptedSubmit}
                      />
                    ) : (
                      <ShipDialogNewItem
                        key={field.id}
                        index={index}
                        item={field}
                        watchedItem={watchedItem}
                        control={control}
                        formState={formState}
                        hiddenItems={getHiddenItemsForIndex(index)}
                        onRemove={remove}
                        onSerialDrawerOpen={setSerialDrawerOpen}
                        serialDrawerOpen={serialDrawerOpen}
                        onClearErrors={(idx) => {
                          clearErrors(`items.${idx}.serials`);
                        }}
                        invalidSerials={undefined}
                        shipWarehouse={shipWarehouse}
                        inventoryMap={inventoryMap}
                        isLoadingInventory={isLoadingInventory}
                        isFetchingInventory={isFetchingInventory}
                        hasAttemptedSubmit={hasAttemptedSubmit}
                      />
                    );
                  })}
                </Stack>
              </Stack>
              <FormTextField
                fullWidth
                isMultiline
                minimumRows={3}
                name="shipNote"
                label="Notes (optional)"
                placeholder={'Add any notes about this shipment'}
                control={control}
              />
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 2,
            }}
          >
            {inlineErrorMessage && (
              <Alert
                severity="error"
                onClose={() => setInlineErrorMessage(null)}
              >
                {inlineErrorMessage}
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button type="button" onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={shipShipmentMutation.isPending}
                onClick={() => setHasAttemptedSubmit(true)}
              >
                {isEditMode ? 'Update shipment' : 'Confirm shipment'}
              </Button>
            </Box>
          </DialogActions>
        </Box>

        {/* Serial Collection Drawer */}
        {serialDrawerOpen !== null &&
          (() => {
            const currentItem = watchedItems?.[serialDrawerOpen];

            // Build global serials set from all items EXCEPT the current one
            const globalSerialsSet = new Set<string>();
            watchedItems?.forEach((item, index) => {
              if (index !== serialDrawerOpen && item?.serials) {
                item.serials.forEach((serial) => globalSerialsSet.add(serial));
              }
            });

            // Show read-only view in edit mode
            if (isEditMode) {
              return (
                <ViewSerialsDrawer
                  onClose={() => setSerialDrawerOpen(null)}
                  partSku={currentItem?.item?.sku || ''}
                  partName={currentItem?.item?.name ?? ''}
                  serials={currentItem?.serials ?? []}
                  invalidSerials={undefined}
                />
              );
            }

            // Show editable drawer when marking as shipped
            return (
              <SerialCollectionDrawer
                isOpen={true}
                onClose={() => setSerialDrawerOpen(null)}
                itemId={itemsField[serialDrawerOpen]?.id ?? ''}
                partSku={currentItem?.item?.sku || ''}
                partName={currentItem?.item?.name ?? ''}
                receivedQuantity={currentItem?.quantity ?? 0}
                serials={currentItem?.serials ?? []}
                onSerialsChange={(serials) => {
                  if (serialDrawerOpen !== null) {
                    setValue(`items.${serialDrawerOpen}.serials`, serials, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: false,
                    });
                    clearErrors(`items.${serialDrawerOpen}.serials`);
                  }
                }}
                invalidSerials={undefined}
                globalSerials={globalSerialsSet}
                locationId={getValues('shipWarehouse')?.id}
                productId={
                  currentItem?.item?.hasAssets && currentItem?.item?.isPhysical
                    ? currentItem?.item?.id
                    : undefined
                }
                availableSerials={availableSerials}
              />
            );
          })()}
      </Dialog>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          shipShipmentMutation.reset();
        }}
        message="An error occurred with the shipment."
      />
    </>
  );
}

// Wrapper component that forces remount when shipment changes
export function OutboundShipDialog(props: Props) {
  // Dialog isn't valid without a shipment
  if (!props.shipment) {
    return null;
  }

  // Force complete remount when shipment key or shippedAt changes to ensure clean state
  const componentKey = `${props.shipment.key}-${props.shipment.shippedAt?.toMillis() || 'not-shipped'}`;
  return <OutboundShipDialogForm key={componentKey} {...props} />;
}
